import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { DropdownInputWithIcon } from "../../components/DropdownInputWithIcon/DropdownInputWithIcon";
import { TextInputPassword } from "../../components/TextInputPassword/TextInputPassword";
import { User, UserStatus } from "../../models/User";
import { Warehouse } from "../../models/Warehouse";
import { Role, RoleType } from "../../models/Role";
import { City } from "../../models/City";
import { activeUser } from "../../services/AuthService";
import {
  getAllCities,
  createCity,
  updateOneCity,
} from "../../services/CityService";
import { getAllRoles } from "../../services/RoleService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { createUser, updateOneUser } from "../../services/UserService";
import "./NewUserForm.scss";

export interface NewUserFormProps {
  show?: string;
  user?: User;
  buttonLabel?: string;
  buttonIcon?: string;
  buttonStyle?: React.CSSProperties;
  buttonSaveStyle?: React.CSSProperties;
  buttonNewCityStyle?: React.CSSProperties;
  buttonInactiveStyle?: React.CSSProperties;
  refreshingData?: () => void;
}

const emptyUser = (): User =>
  ({
    id: "",
    firstName: "",
    lastName: "",
    address: "",
    city: undefined,
    nationalIdNumber: "",
    nationalIdIssueLocation: "",
    personalCodeNumber: "",
    email: "",
    role: undefined,
    warehouse: undefined,
    username: "",
    password: "",
    status: UserStatus.Active,
  } as unknown as User);

export const NewUserForm = (p: NewUserFormProps) => {
  const {
    user,
    show,
    buttonLabel,
    buttonIcon,
    buttonStyle,
    buttonSaveStyle,
    buttonNewCityStyle,
    buttonInactiveStyle,
    refreshingData,
  } = p;

  const { t } = useTranslation();
  const toast = useRef(null);

  const [visibleNewUserForm, setVisibleNewUserForm] = useState<boolean>(false);
  const [citiesApi, setCitiesApi] = useState<City[]>([]);
  const [rolesApi, setRolesApi] = useState<Role[]>([]);
  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [newUser, setNewUser] = useState<User>(() => {
    if (user) return user;
    return emptyUser();
  });
  const [visibleNewCityAddForm, setVisibleNewCityAddForm] =
    useState<boolean>(false);
  const [newCity, setNewCity] = useState<City>({
    code: "",
    name: "",
    id: "",
  });

  const canEdit = activeUser.role?.name === RoleType.Superadmin;

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshCities = async () => {
    try {
      const resp = await getAllCities();
      const list: City[] = resp?.data?.result ?? resp?.data ?? [];
      setCitiesApi(list);
    } catch {
      //@ts-ignore
      toast.current?.show({
        severity: "error",
        detail: String(t("Greška pri učitavanju mesta")),
        life: 3000,
      });
    }
  };

  const refreshRoles = async () => {
    try {
      const resp = await getAllRoles();
      const list: Role[] = resp?.data?.result ?? resp?.data ?? [];
      setRolesApi(list);
    } catch {
      //@ts-ignore
      toast.current?.show({
        severity: "error",
        detail: String(t("Greška pri učitavanju uloga")),
        life: 3000,
      });
    }
  };

  const refreshWarehouses = async () => {
    try {
      const resp = await getAllWarehouses();
      const list: Warehouse[] = resp?.data?.result ?? resp?.data ?? [];
      setWarehousesApi(list);
    } catch {
      //@ts-ignore
      toast.current?.show({
        severity: "error",
        detail: String(t("Greška pri učitavanju magacina")),
        life: 3000,
      });
    }
  };

  useEffect(() => {
    Promise.all([refreshCities(), refreshRoles(), refreshWarehouses()]).catch(
      () => {}
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (show) {
      setVisibleNewUserForm(true);
    }
  }, [show]);

  useEffect(() => {
    if (user) {
      setNewUser(user);
      reset();
    }
  }, [user, reset]);

  const toggleModal = () => {
    setVisibleNewUserForm(!visibleNewUserForm ? true : false);
  };

  const handleNewUserForm = (prop: string, value: string) => {
    setNewUser((newUser) => ({
      ...newUser,
      [prop]: value,
    }));
  };

  const handleCityChange = (_prop: string, value: any) => {
    const found = citiesApi.find((c) => c.id == value);
    setNewUser((prev) => ({ ...prev, city: found }));
  };

  const handleRoleChange = (_prop: string, value: any) => {
    const found = rolesApi.find((r) => r.id == value);
    const next = cloneDeep(newUser);
    next.role = found;

    if (found?.name !== RoleType.Warehouseman) {
      next.warehouse = undefined as any;
    }
    setNewUser(next);
  };

  const handleWarehouseChange = (_prop: string, value: any) => {
    const found = warehousesApi.find((w) => w.id == value);
    setNewUser((prev) => ({ ...prev, warehouse: found as any }));
  };

  const handleNewUserStatus = () => {
    setNewUser((prev) => ({
      ...prev,
      status:
        prev.status === UserStatus.Active
          ? UserStatus.Blocked
          : UserStatus.Active,
    }));
  };

  const handleNewCity = (prop: string, value: string) => {
    if (prop === "newCityName") setNewCity((c) => ({ ...c, name: value }));
    else setNewCity((c) => ({ ...c, code: value }));
  };

  const addNewCity = async () => {
    try {
      if (newCity?.id) {
        await updateOneCity(newCity.id, newCity);
      } else {
        // backend generiše UUID, mi ne šaljemo id
        const payload: Partial<City> = {
          name: newCity.name,
          code: newCity.code,
        };
        const resp = await createCity(payload);
        ensureSuccess(resp);
      }

      setVisibleNewCityAddForm(false);
      setNewCity({ id: "", code: "", name: "" });

      await refreshCities();

      //@ts-ignore
      toast.current?.show({
        severity: "success",
        detail: String(t("Mesto je uspešno sačuvano")),
        life: 2500,
      });
    } catch (e: any) {
      //@ts-ignore
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ?? String(t("Greška pri čuvanju mesta")),
        life: 3000,
      });
    }
  };

  const sanitizeUserPayload = (u: User): Partial<User> => {
    const payload: any = { ...u };

    // id: kod create NE šaljemo
    if (!payload?.id) delete payload.id;

    // city/role/warehouse: ne šaljemo prazne objekte / prazne id
    if (!payload?.city?.id) delete payload.city;
    if (!payload?.role?.id) delete payload.role;

    // warehouse je obavezan samo za Warehouseman
    if (payload?.role?.name === RoleType.Warehouseman) {
      if (!payload.warehouse?.id) delete payload.warehouse;
    } else {
      delete payload.warehouse;
    }

    // password: za update pošalji samo ako je user uneo nešto (da ne pregazi na "")
    if (payload?.id && !payload.password) {
      delete payload.password;
    }

    return payload;
  };

  const onSubmit = async () => {
    try {
      const payload = sanitizeUserPayload(newUser);

      if (newUser?.id) {
        const resp = await updateOneUser(newUser.id, payload);
        ensureSuccess(resp);
      } else {
        const resp = await createUser(payload);
        ensureSuccess(resp);
      }

      //@ts-ignore
      toast.current?.show({
        severity: "success",
        detail: String(t("Uspešno sačuvani podaci")),
        life: 3000,
      });

      refreshingData?.();
      setNewUser(emptyUser());
      setVisibleNewUserForm(false);
    } catch (e: any) {
      //@ts-ignore
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          String(t("Greška pri čuvanju korisnika")),
        life: 3500,
      });
    }
  };

  const onError = () => {
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  const handleResetModal = () => {
    setNewUser({
      id: "",
      firstName: "",
      lastName: "",
      address: "",
      city: {},
      nationalIdNumber: "",
      nationalIdIssueLocation: "",
      personalCodeNumber: "",
      email: "",
      role: {} as Role,
      warehouse: {} as Warehouse,
      username: "",
      password: "",
      status: UserStatus.Active,
    });
    toggleModal();
  };

  let addButtonStyle: React.CSSProperties = {};
  if (buttonStyle) {
    addButtonStyle = buttonStyle;
  } else {
    addButtonStyle.background = "rgba(0, 165, 83, 1)";
    addButtonStyle.border = "none";
  }

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
  }

  let addButtonNewCityStyle: React.CSSProperties = {};
  if (buttonNewCityStyle) {
    addButtonNewCityStyle = buttonNewCityStyle;
  } else {
    addButtonNewCityStyle.background = "rgba(21, 115, 71, 1)";
    addButtonNewCityStyle.border = "none";
  }

  let addButtonInactiveStyle: React.CSSProperties = {};
  if (buttonInactiveStyle) {
    addButtonInactiveStyle = buttonInactiveStyle;
  } else {
    addButtonInactiveStyle.background = "rgba(108, 117, 125, 1)";
    addButtonInactiveStyle.border = "none";
  }

  const header = (
    <div className="header">
      <span className="p-dialog-titlebar-icon p-link">
        <i className="bi bi-person-plus" style={{ fontSize: 18 }}></i>
      </span>
      {newUser?.id === "" ? (
        <span>{t("Dodavanje novog korisnika u sistem")}</span>
      ) : (
        <span>{t("Ažuriranje podataka korisnika")}</span>
      )}
    </div>
  );

  const footer = (
    <div className="footer-buttons">
      <ButtonInput
        labelText={t("Sačuvaj")}
        onButtonClick={handleSubmit(onSubmit, onError)}
        addStyle={addButtonSaveStyle}
        disabled={activeUser.role?.name !== RoleType.Superadmin}
      />
      <ButtonInput
        labelText={t("Otkaži")}
        onButtonClick={handleResetModal}
        addStyle={addButtonInactiveStyle}
        disabled={activeUser.role?.name !== RoleType.Superadmin}
      />
    </div>
  );

  return (
    <>
      <ButtonInput
        labelText={buttonLabel as string}
        icon={buttonIcon}
        onButtonClick={() => {
          handleResetModal();
          reset();
        }}
        addStyle={addButtonStyle}
      />
      <Toast ref={toast} position={"top-right"} />
      {visibleNewUserForm ? (
        <div>
          <Dialog
            header={header}
            visible={true}
            style={{ width: "50vw" }}
            className="new-user-modal"
            footer={footer}
            blockScroll={false}
            draggable={false}
            onHide={toggleModal}
          >
            <div className="new-user-form-body">
              <TextInputWithIcon
                id="firstName"
                value={newUser.firstName as string}
                onValueChange={handleNewUserForm}
                labelText={`${t("Ime korisnika")} *`}
                icon="bi bi-person"
                iconAlign="left"
                placeholderText={t("Unesite ime korisnika")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={!canEdit}
              />
              <TextInputWithIcon
                id="lastName"
                value={newUser.lastName as string}
                onValueChange={handleNewUserForm}
                labelText={`${t("Prezime korisnika")} *`}
                icon="bi bi-person"
                iconAlign="left"
                placeholderText={t("Unesite prezime korisnika")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={!canEdit}
              />
              <TextInputWithIcon
                id="address"
                value={newUser.address as string}
                onValueChange={handleNewUserForm}
                labelText={`${t("Adresa korisnika")} *`}
                icon="bi bi-signpost"
                iconAlign="left"
                placeholderText={t("Unesite ulicu i broj")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={!canEdit}
              />
              <div className="city-content">
                <div className="city-id-content">
                  <DropdownInputWithIcon
                    id="cityId"
                    value={newUser?.city?.id}
                    onValueChange={handleCityChange}
                    options={citiesApi}
                    optionValue="id"
                    optionLabel="name"
                    icon="bi bi-signpost"
                    filter={true}
                    labelText={`${t("Mesto/Naselje korisnika")} *`}
                    placeholderText={t("Mesto/Naselje korisnika")}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    rules={{ required: true }}
                    disabled={!canEdit}
                  />
                </div>
                <div className="city-add-button">
                  <ButtonInput
                    labelText={t("Dodaj mesto")}
                    onButtonClick={() => {
                      setVisibleNewCityAddForm(
                        !visibleNewCityAddForm ? true : false
                      );
                    }}
                    icon="bi bi-plus-square"
                    iconAlign="left"
                    addStyle={addButtonNewCityStyle}
                    disabled={!canEdit}
                  />
                </div>
                {visibleNewCityAddForm && (
                  <div className="new-city-name">
                    <TextInputWithIcon
                      id="newCityName"
                      value={newCity.name}
                      onValueChange={handleNewCity}
                      labelText={t("Naziv mesta/naselja")}
                      icon="bi bi-signpost"
                      iconAlign="left"
                      placeholderText={t("Unesite mesto/naselje")}
                      control={control}
                      errors={errors}
                    />
                  </div>
                )}
                {visibleNewCityAddForm && (
                  <div className="new-city-post-id">
                    <TextInputWithIcon
                      id="newCityPostId"
                      value={newCity.code}
                      onValueChange={handleNewCity}
                      labelText={t("Poštanski broj mesta/naselja")}
                      icon="bi bi-signpost"
                      iconAlign="left"
                      placeholderText={t("Unestite poštanski broj")}
                      control={control}
                      errors={errors}
                    />
                  </div>
                )}
                {visibleNewCityAddForm && (
                  <div className="new-city-add-button">
                    <ButtonInput
                      labelText={t("Dodaj")}
                      onButtonClick={addNewCity}
                      addStyle={addButtonNewCityStyle}
                    />
                  </div>
                )}
              </div>
              <div className="personal-card-content">
                <TextInputWithIcon
                  id="nationalIdNumber"
                  value={newUser.nationalIdNumber as string}
                  onValueChange={handleNewUserForm}
                  labelText={t("Broj lične karte")}
                  icon="bi bi-person-badge"
                  iconAlign="left"
                  placeholderText={t("Unesite broj lične karte")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={!canEdit}
                />
                <TextInputWithIcon
                  id="nationalIdIssueLocation"
                  value={newUser.nationalIdIssueLocation as string}
                  onValueChange={handleNewUserForm}
                  labelText={t("SUP")}
                  icon="bi bi-person-badge"
                  iconAlign="left"
                  placeholderText={t("Unesite SUP")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={!canEdit}
                />
              </div>
              <TextInputWithIcon
                id="personalCodeNumber"
                value={newUser.personalCodeNumber as string}
                onValueChange={handleNewUserForm}
                labelText={t("JMBG korisnika")}
                icon="bi bi-person-badge"
                iconAlign="left"
                placeholderText={t("Unesite JMBG korisnika")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={!canEdit}
              />
              <TextInputWithIcon
                id="email"
                value={newUser.email as string}
                onValueChange={handleNewUserForm}
                labelText={`${t("E - mail adresa")} *`}
                icon="bi bi-envelope"
                iconAlign="left"
                placeholderText={t("Unesite e-mail adresu")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  required: t("Obavezno polje"),
                  pattern: {
                    value:
                      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: t("Pogrešan format Email-a"),
                  },
                }}
                disabled={!canEdit}
              />
              <DropdownInputWithIcon
                id="role"
                value={newUser?.role?.id ?? ""}
                onValueChange={handleRoleChange}
                options={rolesApi}
                optionValue="id"
                optionLabel="name"
                icon="bi bi-person-check"
                labelText={`${t("Izaberite ulogu korisnika")} *`}
                placeholderText={t("Izaberite ulogu korisnika")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={!canEdit}
              />
              {newUser.role?.name === RoleType.Warehouseman && (
                <div className="empty-field"></div>
              )}
              {newUser.role?.name === RoleType.Warehouseman && (
                <DropdownInputWithIcon
                  id="warehouse" //@ts-ignore
                  value={newUser?.warehouse.id ?? ""}
                  onValueChange={handleWarehouseChange}
                  optionValue="id"
                  optionLabel="name"
                  options={warehousesApi.filter((item) => item.virtual !== true)}
                  filter={true}
                  labelText="Magacin *"
                  icon="bi bi-person"
                  placeholderText="Izaberite magacin"
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{ required: true }}
                  disabled={!canEdit}
                />
              )}
              <TextInputWithIcon
                id="username"
                value={newUser.username as string}
                onValueChange={handleNewUserForm}
                labelText={`${t("Korisničko ime")} *`}
                icon="bi bi-person"
                iconAlign="left"
                placeholderText={t("Korisničko ime")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={!canEdit}
              />
              <TextInputPassword
                id="password"
                value={newUser.password as string}
                onValueChange={handleNewUserForm}
                labelText={`${t("Lozinka")} *`}
                icon="bi bi-person"
                iconAlign="left"
                placeholderText={t("Kreirajte lozinku")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: false }}
                disabled={!canEdit}
                feedback={true}
              />
              <RadioButtonInput
                id="status"
                value={newUser.status === UserStatus.Active}
                radioButtonValue1={t("Aktivan")}
                radioButtonValue2={t("Blokiran")}
                onValueChange={handleNewUserStatus}
                labelText={t("Status korisnika")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={!canEdit}
              />
            </div>
          </Dialog>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
